import { Article } from '@wix/answers-api';
import { pageClick } from '@wix/bi-logger-customer-care-data/v2';
import { Box, Divider, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { Collections, ArrowRightSmall } from '@wix/wix-ui-icons-common';
import { useRouter } from 'next/router';
import React from 'react';

import { BI } from '../../../constants';
import { DATA_HOOKS } from '../../../dataHooks';
import { useBI } from '../../../hooks/useBI';
import { mapArticleTypeToItemType } from '../../../utils/bi';
import { useStore } from '../../../zustand/stores';


import css from './index.module.scss';

interface RelatedArticlesMiniProps {
  relatedArticles?: Article[];
  article: Article;
  selfArticleRedirect: (url: string, shallow?: boolean, targetLocale?: string) => void;
}

export const RelatedArticlesMini: React.FC<RelatedArticlesMiniProps> =
  ({ relatedArticles, article, selfArticleRedirect }) => {
    const { t } = useTranslation();
    const { locale } = useRouter();
    const { user } = useStore((state) => state.userSlice);
    const { sendBIEvent } = useBI();


    if (user || !(relatedArticles && relatedArticles.length > 0)) {
      return null;
    }
    const relatedArticleClick = async (
      event: MouseEvent | KeyboardEvent,
      clickedArticle: Article,
      order: string
    ) => {
      const text = (event.target as HTMLElement).textContent as string;
      await sendBIEvent(
        pageClick({
          source_name: BI.SOURCE_NAMES.ARTICLE,
          kb_lang: locale as string,
          item_id: article.id,
          item_type: mapArticleTypeToItemType(article.type),
          clicked_item_type: BI.CLICKED_ITEM_TYPES.RELATED_CONTENT_HELPMATE,
          clicked_url: `/${locale}${clickedArticle.uri}`,
          clicked_text: text,
          clicked_item_order: order,
          clicked_item_id: clickedArticle.id,
          is_page_content_click: false,
        })
      );
      selfArticleRedirect(
        `/${locale}${clickedArticle.uri}`,
        false,
        clickedArticle.locale
      );
    };

    return (
      <Box
        className={css.relatedArticlesMiniContainer}
      >
        <Box className={css.wrapper} direction="vertical">
          <Box className={css.titleContainer} verticalAlign="middle">
            <Collections />
            <Text size="small" className={css.title}>
              {t('article-page.copilot.related-articles.title')}
            </Text>
          </Box>
          <Box
            className={css.summaryContainer}
            direction='vertical'
          >
            {relatedArticles.map((clickedArticle, index) => (
              <div key={clickedArticle.id}
                onClick={async (e: React.MouseEvent<HTMLDivElement>) =>
                  relatedArticleClick(e.nativeEvent, clickedArticle, `${index + 1}`)}
              >
                <Box dataHook={DATA_HOOKS.RELATED_ARTICLE_LINK} className={css.relatedArticleTitle} gap="SP1">
                  <Text className={css.titleText} size="small" weight="thin" ellipsis maxLines={1}>
                    {clickedArticle.title}
                  </Text>
                  <ArrowRightSmall width={18} height={18} />
                </Box>
                {index !== relatedArticles.length - 1 && <Divider className={css.divider} skin="light" />}
              </div>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };
